import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';
import { Title } from 'react-head';
import { useMsal } from '@azure/msal-react';
import { setURLConnectionConfig } from './services/connections';
import useAcquireTokenSilent from './hooks/useAcquireTokenSilent';
import { useFeatureTogglesInterval } from './hooks/useFeatureToggle';
import useCustomNavigationClient from './hooks/useCustomNavigationClient';
import useConnectionConfigSetup from './hooks/useConnectionConfigSetup';
import useFetchCommonMetaData from './hooks/useFetchCommonMetaData';
import SplashScreen from './components/layout/SplashScreen';
import StatusBar from './components/layout/StatusBar';
import MainRouterOutlet from './components/utils/MainRouterOutlet';
import NavigationBar from './components/layout/NavigationBar';
import useUserPermissions from './hooks/useUserPermissions';
import useAcquireArcGISToken from './hooks/useAcquireArcGISToken';
import { reset } from './store/powerBi/powerBiSlice';
import { APP_NAME as appName } from './constants/autoforms';
import { useAxiosInterceptors } from 'hooks/useAxiosInterceptors';

import Highcharts from 'highcharts/highstock';
import exporting from 'highcharts/modules/exporting';
import offlineExporting from 'highcharts/modules/offline-exporting';
import exportData from 'highcharts/modules/export-data';
import annotations from 'highcharts/modules/annotations';
import accessibility from 'highcharts/modules/accessibility';

const App = () => {
    // load Highcharts modules
    exporting(Highcharts);
    exportData(Highcharts);
    annotations(Highcharts);
    offlineExporting(Highcharts);
    accessibility(Highcharts);

    const [isConnectionConfigValid, setIsConnectionConfigValid] = useState(false);

    useAxiosInterceptors();
    useAcquireTokenSilent();
    useFeatureTogglesInterval(isConnectionConfigValid); //turns on refreshing the feature toggles list

    // setup client-side routing for Msal
    useCustomNavigationClient();

    // validate and setup UI version and connection config in axios headers & redux store
    // and redirect based on the auth status
    const { isValidatingConnection, version } = useConnectionConfigSetup();

    // if we have a valid connection retrieve common metadata and feature flags
    useFetchCommonMetaData(isConnectionConfigValid);

    const { accounts } = useMsal();
    const { retrieveUserConnectionPermissions } = useUserPermissions();

    const { isLoading } = useSelector((state) => state.permissions);
    const connectionConfig = useSelector((state) => state.connections);

    const dispatch = useDispatch();

    // retrieve and register ArcGIS OAuth 2.0 token to view web map and feature layers
    useAcquireArcGISToken();

    useEffect(() => {
        const { connection, environment, book } = connectionConfig;
        if (connection && environment && book) {
            // Connection info found
            setIsConnectionConfigValid(true);
            retrieveUserConnectionPermissions();
            // Reset Power BI state to initialState
            dispatch(reset({ isHardReset: true }));
            redirectToSelectedOrDefautVersion();
        }

        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connectionConfig, retrieveUserConnectionPermissions]);

    const redirectToSelectedOrDefautVersion = () => {
        const { connection, environment, book, schemaGroupId, isDefaultConfig } = connectionConfig;
        const pathname = window.location.pathname;
        // If user wants to go to "/connection" page then do not redirect
        // Connection page will clear localStorage defaults
        if (pathname?.trim().toLowerCase() === '/connection') {
            return;
        }

        // Check if we have a valid connection from either query strings or from localStorage
        if (connection && environment && book && schemaGroupId) {
            const versionName = version?.name?.toString();
            const currentUrl = window.location.origin + process.env.PUBLIC_URL;

            if (version?.url && currentUrl.toUpperCase() !== version.url.toUpperCase()) {
                // Redirect to selected or default UI version's url if it is valid and different from current path url
                let connectionConfigParams = new URLSearchParams({ versionName });

                if (!isDefaultConfig) {
                    connectionConfigParams = new URLSearchParams({
                        connection,
                        environment,
                        book,
                        schemaGroupId,
                        isDefaultConfig,
                        localAccountId: accounts[0].localAccountId,
                        versionName,
                    });
                }

                const newUrl = version.url;
                const gotoUrl = setURLConnectionConfig(connectionConfigParams, newUrl);

                window.location.assign(gotoUrl.href);
            }
        }
    };

    return (
        <>
            <Title>{appName}</Title>

            {isValidatingConnection || isLoading ? (
                <SplashScreen />
            ) : (
                <MainRouterOutlet>
                    <NavigationBar />
                    <Outlet />
                    <StatusBar />
                </MainRouterOutlet>
            )}
        </>
    );
};

export default App;
